import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { AuctionServices } from "../../../services/AllServices";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate, useParams } from "react-router-dom";
import { Loader } from "../../../components/CustomLoader";
import errorHandler from "../../../utils/errorHandler";
import CardLayout from "../../../layouts/CardLayout";
import { capitalize } from "../../../utils/helpers";
import DataTable from "react-data-table-component";
import CloseIcon from "@mui/icons-material/Close";
import { debounce } from "lodash";
import { Chip } from "@mui/material";

export default function TicketQuantity() {
  const { id, auctionId, startDate } = useParams();
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [auctionStatus, setAuctionStatus] = useState([]);
  const [auctionStartDateFilter, setAuctionStartDateFilter] = useState(() => {
  return startDate ? new Date(startDate).toISOString().split("T")[0] : "";
});

  const fetchTicketQuantity = async (query) => {
    const body = {
      roomId: id,
      auctionId: auctionId,
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      auctionStatus: auctionStatus,
      auctionStartDate: auctionStartDateFilter,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await AuctionServices.allTcketQuantity(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.winners);
          setTotalPages(res?.data?.totalWinners);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchTicketQuantity(query), 400),
    [currentPage, pageSize, auctionStatus]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    fetchTicketQuantity(searchItem);
  }, [currentPage, pageSize, refresh, auctionStatus,auctionStartDateFilter]);

const columns = [
  {
    name: "S.No.",
    cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
  },
  {
    name: "Ticket Name",
    sortable: true,
    selector: (row) => capitalize(row?.ticketName),
  },
 
{
  name: "Highest Bidder",
  sortable: true,
  cell: (row) => {
    const isCorrupted = row?.isCurrepted === true;

    const isSoldOut =
      !row?.highestBidder &&
      (row?.paymentStatus === "Completed" || row?.paymentStatus === "Transfer Complete");

    const isRunning =
      !row?.highestBidder &&
      !(row?.paymentStatus === "Completed" || row?.paymentStatus === "Transfer Complete");

    let displayText = capitalize(row?.highestBidder || "");
    let borderColor = "#e0e0e0";
    let backgroundColor = "#000";
    let textColor = "inherit";

    if (isCorrupted) {
      displayText = "Corrupted";
      borderColor = "2px solid #b71c1c";
      backgroundColor = "#ffebee";
      textColor = "#b71c1c";
    } else if (isSoldOut) {
      displayText = "Sold Out";
      borderColor = "2px solid red";
      backgroundColor = "#ffeaea";
      textColor = "#d32f2f";
    } else if (isRunning) {
      displayText = "Running";
      borderColor = "2px solid red";
      backgroundColor = "#ffeaea";
      textColor = "#d32f2f";
    }

    return (
      <Box
        sx={{
          border: borderColor,
          padding: "4px 10px",
          borderRadius: "6px",
          backgroundColor: backgroundColor,
          fontWeight: 500,
          color: textColor,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {isCorrupted && <span style={{ fontSize: "16px" }}>❌</span>}
        {displayText}
      </Box>
    );
  },
},
  {
    name: "Price",
    sortable: true,
    selector: (row) => `$${row?.price}`,
  },
  {
    name: "Highest Bid",
    sortable: true,
    selector: (row) => `$${row?.currentBid}`,
  },
  {
    name: "Payment Status",
    sortable: true,
    cell: (row) => (
      <Chip
        label={capitalize(row?.paymentStatus)}
        color={row?.paymentStatus === "paid" ? "success" : "error"}
        variant="outlined"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    ),
  },
  {
    name: "Action",
    center: true,
    cell: (row) => (
      <Tooltip title="View" placement="top">
        <IconButton
          color="primary"
          onClick={() => {
            navigate(
              `/bidding-control/bidding-history/${id}/${auctionId}/${row?.ticketNumber}`
            );
          }}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    ),
  },
];


  return (
    <Box>
      <CardLayout>
        <CardHeader title="Tickets Quantity" />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Input
            value={searchItem}
            placeholder="Search"
            onChange={handleSearchChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear-search"
                  onClick={() => {
                    setSearchItem("");
                    fetchTicketQuantity("");
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            }
          />
          <Input
            type="date"
            value={auctionStartDateFilter}
            onChange={(e) => {
              setAuctionStartDateFilter(e.target.value);
              setCurrentPage(1);
            }}
            sx={{ mr: 2 }}
          />
        </Box>
        <CardContent>
          <DataTable
            customStyles={{
              headCells: {
                style: {
                  fontSize: "15px",
                  fontWeight: "bold",
                },
              },
            }}
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalPages}
            onChangePage={handlePageChange}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 20, 50]}
            onChangeRowsPerPage={(currentRowsPerPage) =>
              setPageSize(currentRowsPerPage)
            }
            progressPending={isLoading || !loaderDelay}
            progressComponent={<Loader />}
            className="custom"
          />
        </CardContent>
      </CardLayout>
    </Box>
  );
}
